import React, { useState } from "react";
import OrderInformation from "./OrderInformation";
import ShippingInformation from "./ShippingInformation";
import ProductInformation from "./ProductInformation";
import { Check } from "lucide-react";
import { useNavigate } from "react-router";
import { useCreateOrder } from "../../../../api/orders/orders";

const AddNewOrder = () => {
  const [step, setStep] = useState(1);
  const { mutate: createOrder, isLoading } = useCreateOrder();
  const [formData, setFormData] = useState({
    orderNumber: "",
    orderType: "",
    paymentType: "",
    shipmentType: "",
    shippingInfo: {
      CustomerName: "",
      mobile: "",
      email: "",
      address: "",
      address2: "",
      state: "",
      city: "",
      Pincode: "",
    },
    productInfo: [
      {
        productName: "",
        quantity: "",
        price: "",
        discount: "",
        sku: "",
      },
    ],
    dimensions: {
      weight: "",
      length: "",
      breadth: "",
      height: "",
    },
    paymentData: {
      paymentMethods: [],
      totalAmount: 0,
    },
  });

  const navigate = useNavigate();

  const steps = [
    { title: "Step 1", subtitle: "Order Details" },
    { title: "Step 2", subtitle: "Shipping Details" },
    { title: "Step 3", subtitle: "Product Details" },
  ];

  const goNext = (dataFromStep) => {
    if (step === 3) {
      const updatedData = {
        ...formData,
        productInfo: dataFromStep.productInfo,
        dimensions: dataFromStep.dimensions,
        totalAmount: dataFromStep.totalAmount,
        paymentData: dataFromStep.paymentData,
      };

      setFormData(updatedData);
      handleFinalSubmit(updatedData);
    } else {
      setFormData((prev) => {
        let updatedData = { ...prev };

        if (step === 1) {
          updatedData = { ...updatedData, ...dataFromStep };
        } else if (step === 2) {
          updatedData.shippingInfo = dataFromStep.shippingInfo;
        }

        return updatedData;
      });

      setStep((prev) => prev + 1);
    }
  };

  const goBack = () => setStep((prev) => Math.max(prev - 1, 1));
  const handleGoBack = () => navigate("/orders");

  const handleFinalSubmit = (formData) => {
    if (!formData) return console.error("formData is undefined!");

    // Default payment methods (always included)
    const defaultPaymentMethods = [
      {
        paymentOption: "Clicktocart",
        paymentAmount: 0,
        paymentProofImages: [],
      },
      {
        paymentOption: "Swapnil Gautam",
        paymentAmount: 0,
        paymentProofImages: [],
      },
    ];

    // Merge user-selected methods with defaults
    const mergedPaymentMethods = defaultPaymentMethods.map((defaultMethod) => {
      const existing = formData.paymentData.paymentMethods?.find(
        (m) => m.paymentOption === defaultMethod.paymentOption
      );
      return existing
        ? {
            ...defaultMethod,
            paymentAmount: existing.paymentAmount || 0,
            paymentProofImages: existing.paymentProofImages || [],
          }
        : defaultMethod;
    });

    // products array
    const productsArray = formData.productInfo.map((p) => ({
      sku: p.sku.trim(),
      name: p.productName,
      quantity: Number(p.quantity),
      unit_price: Number(p.price),
      discount_amount: Number(p.discount) || 0,
      is_tax_inclusive: false,
      tax_rate: 0,
    }));

    const jsonData = {
      order_number: formData.orderNumber || `TRL-${Date.now()}`,
      payment_mode: formData.paymentType?.toLowerCase() || "cod",
      is_reverse: formData.orderType?.toLowerCase() === "reverse",
      
      // Customer info
      customer_name: formData.shippingInfo.CustomerName,
      customer_email: formData.shippingInfo.email,
      customer_phone_number: formData.shippingInfo.mobile,
      customer_isd_code: "91",

      // Shipping address
      shipping_name: formData.shippingInfo.CustomerName,
      shipping_email: formData.shippingInfo.email,
      shipping_phone_number: formData.shippingInfo.mobile,
      shipping_isd_code: "91",
      shipping_address_line1: formData.shippingInfo.address,
      shipping_address_line2: formData.shippingInfo.address2 || "",
      shipping_city: formData.shippingInfo.city,
      shipping_state: formData.shippingInfo.state,
      shipping_pincode: formData.shippingInfo.Pincode,
      shipping_country: "India",

      // Billing address (same as shipping)
      billing_name: formData.shippingInfo.CustomerName,
      billing_email: formData.shippingInfo.email,
      billing_phone_number: formData.shippingInfo.mobile,
      billing_isd_code: "91",
      billing_address_line1: formData.shippingInfo.address,
      billing_address_line2: formData.shippingInfo.address2 || "",
      billing_city: formData.shippingInfo.city,
      billing_state: formData.shippingInfo.state,
      billing_pincode: formData.shippingInfo.Pincode,
      billing_country: "India",

      // Package dimensions
      package_weight_in_gm: Number(formData.dimensions.weight) * 1000,
      package_weight_in_kg: Number(formData.dimensions.weight),
      package_length_in_cm: Number(formData.dimensions.length),
      package_breadth_in_cm: Number(formData.dimensions.breadth),
      package_height_in_cm: Number(formData.dimensions.height),

      // Products as array
      products: productsArray,

      // Amounts
      subtotal_amount: Number(formData.totalAmount),
      total_amount: Number(formData.totalAmount),
      shipping_charges: 0,
      discount_amount: 0,

      // COD fields
      is_cod: formData.paymentType?.toLowerCase() === "cod",
      cod_collectable_amount: formData.paymentType?.toLowerCase() === "cod" ? Number(formData.totalAmount) : 0,

      // Tax fields
      tax_amount: 0,
      cgst_amount: 0,
      sgst_amount: 0,
      igst_amount: 0,

      // Warehouse
      warehouse_id: 12837,

      // Payment methods
      paymentMethods: mergedPaymentMethods,
    };

    const submitData = new FormData();
    
    Object.keys(jsonData).forEach((key) => {
      if (key === 'products' || key === 'paymentMethods') {
        
        submitData.append(key, JSON.stringify(jsonData[key]));
      } else {
        submitData.append(key, jsonData[key]);
      }
    });

    mergedPaymentMethods.forEach((method) => {
      if (method.paymentProofImages?.length > 0) {
        method.paymentProofImages.forEach((file, i) => {
          const sanitized = method.paymentOption.replace(/\s+/g, "_");
          const fieldName = `paymentProofImages_${sanitized}_${i}`;
          submitData.append(fieldName, file);
          console.log(`📸 Added file: ${fieldName}`);
        });
      }
    });

    console.log("📦 Submitting Order with products:", productsArray);
    createOrder(submitData);
  };

  return (
    <div className="min-h-screen">
      <div className="max-w-auto mx-auto bg-white rounded-lg shadow-md flex h-[100vh] overflow-hidden">
        {/* Sidebar Stepper */}
        <div className="hidden md:block md:w-1/5 p-6 border-r overflow-y-auto">
          <div className="flex flex-col">
            {steps.map((s, index) => (
              <div key={index} className="flex items-start space-x-3 relative">
                <div className="flex flex-col items-center">
                  <div
                    className={`w-7 h-7 text-sm rounded-full flex items-center justify-center text-white font-medium ${
                      index + 1 === step
                        ? "bg-primary"
                        : index + 1 < step
                        ? "bg-primary"
                        : "bg-gray-300"
                    }`}
                  >
                    {index + 1 < step ? <Check size={14} /> : index + 1}
                  </div>
                  {index < steps.length - 1 && (
                    <div
                      className={`w-px h-16 ${
                        index + 1 < step ? "bg-primary" : "bg-gray-300"
                      }`}
                    ></div>
                  )}
                </div>
                <div>
                  <h4 className="text-base font-medium">{s.title}</h4>
                  <p className="text-xs text-gray-600">{s.subtitle}</p>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Main Content */}
        <div className="w-full md:w-4/5 flex flex-col overflow-y-auto">
          {/* Header */}
          <div className="border-b p-4 md:p-6 sticky top-0 bg-white z-10 flex justify-between items-center">
            <h2 className="text-lg md:text-xl font-semibold text-gray-800">
              Add New Order
            </h2>
            <button
              onClick={handleGoBack}
              className="bg-primaryLight hover:bg-primary text-primary border border-primary hover:text-white px-4 py-1.5 rounded text-sm"
            >
              ⤺ GO BACK
            </button>
          </div>

          {/* Step Content */}
          <div className="p-4 md:p-6 flex-grow overflow-y-auto text-sm">
            {step === 1 && (
              <OrderInformation onNext={goNext} defaultData={formData} />
            )}
            {step === 2 && (
              <ShippingInformation
                onPrevious={goBack}
                onNext={goNext}
                defaultData={formData.shippingInfo}
              />
            )}
            {step === 3 && (
              <ProductInformation
                onPrevious={goBack}
                onNext={(data) => goNext(data)}
                defaultData={formData}
                isLoading={isLoading}
              />
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default AddNewOrder;